import React, { useState, useRef, useEffect } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/auth";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Form, FormControl, FormField, FormItem, FormLabel } from "@/components/ui/form";
import { Eye, EyeOff } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import LanguageSelector from "@/components/layout/LanguageSelector";
import NavbarLogo from "@/components/layout/NavbarLogo";
import Footer from "@/components/layout/Footer";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const AuthPage = () => {
  const { user, signIn, loading } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const hiddenUsernameRef = useRef<HTMLInputElement>(null);
  // Tracks whether the student has manually typed into word1/word2.
  // Once true, late autofill events (Safari/iPad fire `change` on the hidden
  // username field well after the user has started typing) must not overwrite
  // the user's input.
  const studentHasTypedRef = useRef(false);

  // Schemas with localized validation messages
  const loginSchema = z.object({
    email: z.string().email(t("Neplatný e-mail", "Neplatný e-mail")),
    password: z
      .string()
      .min(6, t("Heslo musí mít alespoň 6 znaků", "Heslo musí mať aspoň 6 znakov")),
  });

  const studentCodeSchema = z.object({
    word1: z.string().min(1, t("První slovo je povinné", "Prvé slovo je povinné")),
    word2: z.string().min(1, t("Druhé slovo je povinné", "Druhé slovo je povinné")),
    password: z
      .string()
      .min(6, t("Heslo musí mít alespoň 6 znaků", "Heslo musí mať aspoň 6 znakov")),
  });

  const loginForm = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const studentCodeForm = useForm<z.infer<typeof studentCodeSchema>>({
    resolver: zodResolver(studentCodeSchema),
    defaultValues: {
      word1: "",
      word2: "",
      password: "",
    },
  });

  // Watch word1 and word2 to update the hidden combined username field
  const word1 = studentCodeForm.watch("word1");
  const word2 = studentCodeForm.watch("word2");

  // Update hidden username field when words change
  useEffect(() => {
    if (hiddenUsernameRef.current && (word1 || word2)) {
      hiddenUsernameRef.current.value = `${word1}-${word2}`;
    }
  }, [word1, word2]);

  // Parse a combined username (word1-word2) into separate fields
  const parseAndSetWords = (combinedValue: string) => {
    if (combinedValue && combinedValue.includes("-")) {
      // Split at the first hyphen to get word1 and word2
      const hyphenIndex = combinedValue.indexOf("-");
      const parsedWord1 = combinedValue.substring(0, hyphenIndex);
      const parsedWord2 = combinedValue.substring(hyphenIndex + 1);

      // Update the form fields
      studentCodeForm.setValue("word1", parsedWord1, { shouldValidate: true });
      studentCodeForm.setValue("word2", parsedWord2, { shouldValidate: true });
    }
  };

  // Handle autofill from password manager on the hidden username field.
  // Guarded against late-arriving Safari/iPad autofill: once the student has
  // begun typing into the visible word1/word2 fields, ignore any further
  // hidden-input change events (otherwise autofill would overwrite their
  // input mid-login, which was the cause of the iPad login failures).
  const handleHiddenUsernameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (studentHasTypedRef.current) return;
    const currentWord1 = studentCodeForm.getValues("word1");
    const currentWord2 = studentCodeForm.getValues("word2");
    if (currentWord1 || currentWord2) return;
    parseAndSetWords(e.target.value);
  };

  // Check for autofill that happened before mount (Chrome sometimes fills before React hydrates)
  useEffect(() => {
    const checkAutofill = () => {
      if (studentHasTypedRef.current) return;
      if (hiddenUsernameRef.current?.value) {
        const currentWord1 = studentCodeForm.getValues("word1");
        const currentWord2 = studentCodeForm.getValues("word2");
        // Only parse if the visible fields are still empty
        if (!currentWord1 && !currentWord2) {
          parseAndSetWords(hiddenUsernameRef.current.value);
        }
      }
    };

    // Check immediately and after a short delay (for slow autofill)
    checkAutofill();
    const timeoutId = setTimeout(checkAutofill, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  const onLogin = async (values: z.infer<typeof loginSchema>) => {
    try {
      await signIn(values.email, values.password);
      toast({
        title: t("Vítejte zpět!", "Vitajte späť!"),
        description: t("Byli jste úspěšně přihlášeni", "Boli ste úspešne prihlásení"),
      });
      navigate("/");
    } catch (error) {
      console.error("Chyba přihlášení:", error);
      toast({
        title: t("Chyba přihlášení", "Chyba prihlásenia"),
        description: t("Neplatné přihlašovací údaje", "Neplatné prihlasovacie údaje"),
        variant: "destructive",
      });
    }
  };

  const onStudentCodeLogin = async (values: z.infer<typeof studentCodeSchema>) => {
    try {
      // Sanitize words to create a valid email address (remove diacritics and special chars)
      const sanitizeForEmail = (str: string): string => {
        return str
          .toLowerCase()
          .normalize("NFD") // Decompose combined characters
          .replace(/[\u0300-\u036f]/g, "") // Remove diacritics
          .replace(/[^a-z0-9]/g, "-") // Replace non-alphanumeric with hyphen
          .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, ""); // Remove leading/trailing hyphens
      };

      const sanitizedWord1 = sanitizeForEmail(values.word1);
      const sanitizedWord2 = sanitizeForEmail(values.word2);
      const username = `${sanitizedWord1}-${sanitizedWord2}`;

      const { data, error } = await supabase.auth.signInWithPassword({
        // Use as email for Supabase, a fake email, but we want to make use of the premade session management solutoin
        email: `${username}@student.example.com`,
        password: values.password,
      });

      if (error) {
        console.error("Student login error:", error);
        toast({
          title: t("Chyba přihlášení", "Chyba prihlásenia"),
          description: t("Neplatné přihlašovací údaje", "Neplatné prihlasovacie údaje"),
          variant: "destructive",
        });
        return;
      }

      if (data.user) {
        // The default-password / change-password redirect is handled centrally by
        // <DefaultPasswordGuard> in App.tsx, which uses `isDefaultPassword` from
        // AuthProvider. Doing it here would duplicate the same DB lookups
        // AuthProvider already runs after onAuthStateChange and noticeably slows
        // down login on weaker devices / networks (~3 extra round-trips).
        toast({
          title: t("Přihlášení úspěšné", "Prihlásenie úspešné"),
          description: t("Byli jste úspěšně přihlášeni", "Boli ste úspešne prihlásení"),
        });
        navigate("/");
      }
    } catch (error) {
      console.error("Chyba přihlášení studenta:", error);
      // Toast already shown in the specific error handling above
    }
  };

  // Error handler for validation errors - shows toast with first error
  const onValidationError = (errors: Record<string, { message?: string }>) => {
    const errorKeys = Object.keys(errors);
    if (errorKeys.length > 0) {
      const firstErrorKey = errorKeys[0];
      const firstError = errors[firstErrorKey];

      if (firstError?.message) {
        toast({
          title: t("Chyba validace", "Chyba validácie"),
          description: firstError.message,
          variant: "destructive",
        });
      }
    }
  };

  // Přesměrování, pokud je uživatel již přihlášen
  if (user && !loading) {
    return <Navigate to="/" />;
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Language selector in top right */}
      <div className="absolute top-4 right-4 z-10">
        <LanguageSelector />
      </div>

      {/* Centered login form */}
      <div className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Logo and title */}
          <div className="text-center mb-8">
            <div className="flex items-center justify-center mb-4 scale-150">
              <NavbarLogo />
            </div>
            <p className="text-muted-foreground">
              {t("Pro ty, kteří potřebují vědět víc", "Pre tých, ktorí potrebujú vedieť viac")}
            </p>
          </div>

          <div className="mb-8 text-center">
            <h1 className="text-2xl font-bold">{t("Přihlášení", "Prihlásenie")}</h1>
            <p className="text-muted-foreground mt-2">
              {t("Přihlaste se ke svému účtu", "Prihláste sa do svojho účtu")}
            </p>
          </div>

          <div className="bg-card rounded-lg shadow-sm border p-6">
            <Tabs defaultValue="student">
              <TabsList className="grid grid-cols-2 mb-6 w-full">
                <TabsTrigger value="teacher">{t("Učitel", "Učiteľ")}</TabsTrigger>
                <TabsTrigger value="student">{t("Student", "Študent")}</TabsTrigger>
              </TabsList>

              <TabsContent value="teacher">
                <Form {...loginForm}>
                  <form
                    onSubmit={loginForm.handleSubmit(onLogin, onValidationError)}
                    className="space-y-4"
                  >
                    <FormField
                      control={loginForm.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input placeholder={t("jmeno@email.cz", "meno@email.sk")} {...field} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <div className="flex items-center justify-between">
                            <FormLabel>{t("Heslo", "Heslo")}</FormLabel>
                            <Link
                              to="/forgot-password"
                              className="text-sm text-primary hover:underline"
                            >
                              {t("Zapomněli jste heslo?", "Zabudli ste heslo?")}
                            </Link>
                          </div>
                          <FormControl>
                            <div className="relative">
                              <Input
                                type={showLoginPassword ? "text" : "password"}
                                placeholder="******"
                                {...field}
                              />
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="absolute right-0 top-0 h-full px-3 py-2"
                                onClick={() => setShowLoginPassword(!showLoginPassword)}
                                tabIndex={-1}
                              >
                                {showLoginPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                                <span className="sr-only">
                                  {showLoginPassword
                                    ? t("Skrýt heslo", "Skryť heslo")
                                    : t("Zobrazit heslo", "Zobraziť heslo")}
                                </span>
                              </Button>
                            </div>
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={loginForm.formState.isSubmitting}
                    >
                      {loginForm.formState.isSubmitting
                        ? t("Přihlašování...", "Prihlasovanie...")
                        : t("Přihlásit se", "Prihlásiť sa")}
                    </Button>
                  </form>
                </Form>
              </TabsContent>

              <TabsContent value="student">
                <Form {...studentCodeForm}>
                  <form
                    onSubmit={studentCodeForm.handleSubmit(onStudentCodeLogin, onValidationError)}
                    className="space-y-4"
                  >
                    {/* Hidden combined username field for password manager autofill */}
                    <input
                      ref={hiddenUsernameRef}
                      type="text"
                      name="student-username"
                      id="student-username"
                      autoComplete="username"
                      onChange={handleHiddenUsernameChange}
                      style={{
                        position: "absolute",
                        opacity: 0,
                        width: "1px",
                        height: "1px",
                        padding: 0,
                        margin: 0,
                        overflow: "hidden",
                        pointerEvents: "none",
                      }}
                      tabIndex={-1}
                      aria-hidden="true"
                    />

                    <div className="text-center mb-4">
                      <p className="text-sm text-muted-foreground">
                        {t("Zadejte dvě slova a heslo", "Zadajte dve slová a heslo")}
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <FormField
                        control={studentCodeForm.control}
                        name="word1"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("První slovo", "Prvé slovo")}</FormLabel>
                            <FormControl>
                              <Input
                                id="student-word1"
                                autoComplete="off"
                                placeholder={t("např. veselý", "napr. veselý")}
                                {...field}
                                onKeyDown={() => {
                                  studentHasTypedRef.current = true;
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={studentCodeForm.control}
                        name="word2"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t("Druhé slovo", "Druhé slovo")}</FormLabel>
                            <FormControl>
                              <Input
                                id="student-word2"
                                autoComplete="off"
                                placeholder={t("např. orel", "napr. orol")}
                                {...field}
                                onKeyDown={() => {
                                  studentHasTypedRef.current = true;
                                }}
                              />
                            </FormControl>
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={studentCodeForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>{t("Heslo", "Heslo")}</FormLabel>
                          <FormControl>
                            <Input
                              id="student-password"
                              type={showLoginPassword ? "text" : "password"}
                              autoComplete="current-password"
                              placeholder="******"
                              {...field}
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={studentCodeForm.formState.isSubmitting}
                    >
                      {studentCodeForm.formState.isSubmitting
                        ? t("Přihlašování...", "Prihlasovanie...")
                        : t("Přihlásit se", "Prihlásiť sa")}
                    </Button>
                  </form>
                </Form>
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default AuthPage;
